import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useApp } from './AppContext';
import { useVibeGamification } from './VibeGamificationContext';
import { usePrivacyAuth } from './PrivacyAuthContext';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { assessmentResults, userProgress, completedModules, completedTools } = useApp();
  const { totalXP, currentLevel, unlockedAchievements, currentStreak, longestStreak, VIBE_ACHIEVEMENTS } = useVibeGamification();
  const { user, isLoggedIn } = usePrivacyAuth();

  // Build user profile from existing contexts (memoized for performance)
  const userProfile = useMemo(() => ({
    id: user?.userId || 'anonymous',
    username: user?.username || 'Guest',
    level: currentLevel || 1,
    xp: totalXP || 0,
    currentStreak: currentStreak || 0,
    longestStreak: longestStreak || 0,
    joinedDate: user?.createdAt || new Date().toISOString(),
    isLoggedIn,
    completedModulesCount: completedModules?.length || 0,
    completedToolsCount: completedTools?.length || 0,
    totalAchievements: unlockedAchievements?.length || 0,
    assessmentCompleted: !!assessmentResults,
  }), [user, currentLevel, totalXP, currentStreak, longestStreak, isLoggedIn, completedModules, completedTools, unlockedAchievements, assessmentResults]);

  // Build stats from existing contexts (memoized and reactive)
  const stats = useMemo(() => ({
    // Progress stats
    modulesCompleted: completedModules?.length || 0,
    toolsCompleted: completedTools?.length || 0,
    totalSessions: Object.values(userProgress?.modules || {}).reduce((total, module) => {
      return total + (module.completedMicroSessions?.length || 0);
    }, 0),
    
    // Time-based stats
    daysActive: currentStreak || 0,
    weeklyGoalProgress: Math.min(100, ((currentStreak % 7) / 7) * 100),
    monthlyGoalProgress: Math.min(100, ((completedModules?.length || 0) / 12) * 100),
    
    // XP and progression
    totalXP: totalXP || 0,
    currentLevel: currentLevel || 1,
    xpToNextLevel: Math.max(0, ((currentLevel || 1) * 100) - (totalXP || 0)),
    
    // Achievement stats
    achievementsUnlocked: unlockedAchievements?.length || 0,
    recentAchievements: (unlockedAchievements?.slice(-3) || []).map((achievementId, index) => {
      const achievement = VIBE_ACHIEVEMENTS?.[achievementId];
      if (!achievement) return null;
      return {
        id: achievement.id,
        title: achievement.name,
        description: achievement.description,
        type: achievement.category || 'milestone',
        xpReward: achievement.xp,
        earnedAt: '2024-01-01T00:00:00.000Z', // Static date to prevent refresh loops
      };
    }).filter(Boolean),
    
    // Assessment insights
    assessmentScore: assessmentResults?.totalScore || 0,
    strongAreas: assessmentResults?.strengths || [],
    growthAreas: assessmentResults?.growthAreas || [],
    
    // Usage patterns
    favoriteTimeOfDay: 'evening', // Could be calculated from activity data
    mostUsedTools: [], // Could be calculated from tool usage
    preferredModuleTypes: [], // Could be calculated from module completion
    
    // Recent activity
    lastActivity: userProgress?.lastActivity || new Date().toISOString(),
    recentModules: completedModules?.slice(-3) || [],
    recentTools: completedTools?.slice(-3) || [],
  }), [completedModules, completedTools, userProgress, totalXP, currentLevel, unlockedAchievements, currentStreak, assessmentResults, VIBE_ACHIEVEMENTS]);

  const value = useMemo(() => ({
    userProfile,
    stats,
    isLoading: false, // Could add actual loading states if needed
    error: null,
  }), [userProfile, stats]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;